import { FilterSection } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { allCaseStatusOptions } from "./filterOptions/caseStatusFilterOptions";
import { allNycbsClientOutcomeOptions } from "./filterOptions/nycbsClientOutcomeFilterOptions";
import { nycbsOutcomeClassFilterOptions } from "./filterOptions/nycbsOutcomeClassFilterOptions";
import { allPharmacyTypeOptions } from "./filterOptions/pharmacyTypeFilterOptions";

export enum NycbsExternalFilterKeys {
  DRUG = "drug",
  PHARMACY_TYPE = "pharmacy_type",
  OUTCOME = "outcome",
  OUTCOME_CLASS = "outcome_class",
  CASE_STATUS = "case_status",
}

/**
 * Builds the Drug filter section from unique values in loaded table data.
 *
 * @param data - Accumulated NYCBS External order rows
 * @return FilterSection: Drug filter configuration
 */
export const createFilterForDrug = (
  data: PharmaStpFileModel[],
): FilterSection => {
  const uniqueDrugs = Array.from(
    new Set(
      data
        .map((item) => item?.drug?.trim())
        .filter((drug): drug is string => Boolean(drug)),
    ),
  ).sort();

  return {
    id: NycbsExternalFilterKeys.DRUG,
    label: "Drug",
    options: uniqueDrugs.map((drug) => ({
      label: drug,
      value: drug,
    })),
    type: "string",
  };
};

/**
 * Builds the Pharmacy Type filter section.
 *
 * @param _data - Accumulated NYCBS External order rows (unused)
 * @return FilterSection: Pharmacy Type filter configuration
 */
export const createFilterForPharmacyType = (
  _data: PharmaStpFileModel[],
): FilterSection => {
  return {
    id: NycbsExternalFilterKeys.PHARMACY_TYPE,
    label: "Pharmacy Type",
    options: allPharmacyTypeOptions,
    type: "string",
  };
};

/**
 * Builds the client-facing Outcome filter section.
 *
 * @param _data - Accumulated NYCBS External order rows (unused)
 * @return FilterSection: Outcome filter configuration
 */
export const createFilterForOutcome = (
  _data: PharmaStpFileModel[],
): FilterSection => {
  return {
    id: NycbsExternalFilterKeys.OUTCOME,
    label: "Outcome",
    options: allNycbsClientOutcomeOptions,
    type: "string",
  };
};

/**
 * Builds the Outcome Class filter section.
 *
 * @param _data - Accumulated NYCBS External order rows (unused)
 * @return FilterSection: Outcome Class filter configuration
 */
export const createFilterForOutcomeClass = (
  _data: PharmaStpFileModel[],
): FilterSection => {
  return {
    id: NycbsExternalFilterKeys.OUTCOME_CLASS,
    label: "Outcome Class",
    options: nycbsOutcomeClassFilterOptions,
    type: "string",
  };
};

/**
 * Builds the Case Status (Open/Closed) filter section.
 *
 * @param _data - Accumulated NYCBS External order rows (unused)
 * @return FilterSection: Case Status filter configuration
 */
export const createFilterForCaseStatus = (
  _data: PharmaStpFileModel[],
): FilterSection => {
  return {
    id: NycbsExternalFilterKeys.CASE_STATUS,
    label: "Case Status",
    options: allCaseStatusOptions,
    type: "string",
  };
};

/**
 * Builds all sidebar filter sections for the NYCBS External table.
 *
 * @param data - Accumulated NYCBS External order rows
 * @return FilterSection[]: Filter sections shown in the sidebar
 */
export const createAllFilterSections = (
  data: PharmaStpFileModel[],
): FilterSection[] => {
  if (!data || data.length === 0) {
    return [];
  }

  return [
    createFilterForDrug(data),
    createFilterForPharmacyType(data),
    createFilterForOutcome(data),
    createFilterForOutcomeClass(data),
    createFilterForCaseStatus(data),
  ];
};
